import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Tabs, Checkbox, Radio, message, Button } from 'antd';
import classnames from 'classnames';
import { isEffectArray, getType } from '@/common/utils';
import ComponentLog from '@/components/component-log';
import CrossEmptyList from '../components/cross-empty-list';
import CrossContentTable from '../components/cross-content-table';
import ParentFilter from '../components/cross-parent-filter';
import CrossUserGuide from '../components/cross-user-guide';
import { logRemainCardNum, logSearchActionParams } from '../log';
import { CATEREFTYPEENUM, DEPTIDENUM, CIDSCOPEENUM, REPORT_CONST, USERGUIDEKEY, cutNullOptions } from '../config';
import apis from '../apis';
import './index.less';

/* 列表请求的原始默认配置 */
const optionsForm = {
  mainCid: '',
  mainCidLevel: '',
  refType: 0,
};
const optionsCommon = {
  deptId: '',
  cidScope: 0,
  sortField: '',
  sortOrder: '',
};
const initOptions = { ...optionsForm, ...optionsCommon };

const CrossCategoryMarketing = (props) => {
  const [hasLoad, setHasLoad] = useState(false); //是否完成初始化
  const [categoryList, setCategoryList] = useState([]); //类目
  const [historyData, setHistoryData] = useState({ list: [], actId: '' }); //历史记录的存储
  const [activityId, setActivityId] = useState(''); //当前活跃activityId
  const [crossContentData, setCrossContentData] = useState(null); //查询结果
  const [options, setOptions] = useState({ ...initOptions }); //查询和导出接口参数

  const [parentForm] = Form.useForm();
  const [subForm] = Form.useForm();
  const userGuideRef = useRef(null);

  /***********************************************************
   ************************* 顶层存储 *************************
   ***********************************************************/
  const preSetHistoryData = useCallback(async (postData) => {
    setHistoryData(postData);
    const { ret, data, msg } = await apis.commonServiceReportJson({
      code: REPORT_CONST.CODE,
      op: REPORT_CONST.SETOP,
      data: JSON.stringify(postData),
    });

    if (ret !== '0') {
      message.error(`${msg || '存储推荐列表失败，请您稍后重试，谢谢！'}`);
    }
  }, []);
  /***********************************************************
   *********************** 初始化请求数据 **********************
   ***********************************************************/
  // 用户引导查询
  const getUserGuide = useCallback(async () => {
    const { ret, data, msg } = await apis.userGuide({ key: USERGUIDEKEY });
    ret === '0' && userGuideRef.current.show();
  }, []);
  // 获取类目
  const getCategoryList = useCallback(async () => {
    const { ret, data, msg } = await apis.statisticTree({ type: 1 });
    if (ret && ret === '0') {
      setCategoryList(data);
    } else {
      setCategoryList([]);
    }
  }, []);
  // 获取历史list
  const getHistoryList = useCallback(async () => {
    if (subForm) {
      setHasLoad(false);
      const {
        ret,
        data: { data: cusData },
        msg,
      } = await apis.commonServiceReportJson({
        code: REPORT_CONST.CODE,
        op: REPORT_CONST.GETOP,
        data: '',
      });
      setHasLoad(true);

      if (ret !== '0') {
        message.error(`${msg || '获取推荐列表失败，请您稍后重试，谢谢！'}`);
      }

      if (ret && ret === '0') {
        if (cusData && cusData.length > 0) {
          const backData = JSON.parse(cusData);
          setHistoryData(backData);
          setActivityId(backData?.actId);
          const currentItem = backData.list.find((item) => item.id === backData?.actId);
          setOptions((pre) => ({
            ...optionsCommon,
            sortField: pre.sortField,
            sortOrder: pre.sortOrder,
            mainCid: currentItem?.mainCid,
            mainCidLevel: currentItem?.mainCidLevel,
            refType: currentItem?.refType,
            isNew: false,
          }));
          subForm?.resetFields();
          return false;
        }
      }
      // 不满足条件的，全部初始化
      setHistoryData({ list: [], actId: '' });
      setActivityId('');
    }
  }, [subForm]);

  useEffect(() => {
    getUserGuide();
    getCategoryList();
    getHistoryList();
  }, [getUserGuide, getCategoryList, getHistoryList]);
  /***********************************************************
   ************************ 查询事件响应 ***********************
   ***********************************************************/
  // 一级筛选
  function handleOnParentFormFinish(value) {
    const { mainCid, refType } = value;
    if (!isEffectArray(mainCid)) {
      message.warn('请选择品类！');
      return false;
    }
    if ((refType ?? '') === '') {
      message.warn('请选择商品商品类型！');
      return false;
    }
    const mainCidLevel = mainCid.length;
    const newMainCid = mainCid[mainCidLevel - 1];
    const newMainCidArr = newMainCid?.split('-');
    const computedMainCid = newMainCidArr[newMainCidArr.length - 1];
    const queryObj = { mainCid: computedMainCid, mainCidLevel, refType };
    if (!!queryObj) {
      const fetchObj = {
        ...initOptions,
        sortField: options.sortField,
        sortOrder: options.sortOrder,
        ...queryObj,
        isNew: true,
      };
      !!queryObj && memoizedGetTableListFirstFilter(fetchObj);
    }
  }

  // 专门为一级筛选做的
  // 一级筛选要界定成功和不成功，成功了再去修改，不成功，什么都不动，直接toast即可;同时如果数据为空也是直接toast，页面不做任何处理
  const memoizedGetTableListFirstFilter = useCallback(
    async (fetchObj) => {
      if (!(fetchObj?.mainCid && fetchObj?.mainCidLevel && (fetchObj?.refType ?? '') !== '')) {
        setActivityId('');
        setCrossContentData(null);
        return false;
      }
      const { isNew, ...trueOptions } = fetchObj;
      // 过滤掉非有效的字段【为空的或者空数组、空对象的字段】
      const utilOptions = cutNullOptions(trueOptions);
      try {
        logSearchActionParams(utilOptions);
        const { ret, data, msg } = await apis.technicaCateRecList({ ...utilOptions });
        if (ret && ret === '0') {
          // 非有效数据，长度为空等，直接toast，不做任何处理
          if (!isEffectArray(data?.list)) {
            message.error(`${'暂无匹配结果,请更换品类查询！'}`);
          } else {
            // 保证是有效的，长度大于0，且成功了，再去更改赛选条件
            // 成功的状态里面有一种情况是点击了顶部的查询，这种情况就要看下处理要不要向历史记录里面推一条
            // 还得把子选项清空【这个地方有点搞呀，要先拿子选项清空的状态去请求，当成功了，在去清楚页面上的子状态显示】
            if (isNew) {
              subForm && subForm?.resetFields();
              setOptions(fetchObj);
              setCrossContentData(data);
              data && isEffectArray(data?.list) && handleEffectTags(fetchObj, data);
            }
          }
        } else {
          // 新增时，报错，直接toast，不做任何处理
          message.error(`${msg || '暂无匹配结果,请更换品类查询！'}`);
        }
      } catch (error) {
        console.log(error);
      }

      // 成功查询且是顶级查询时，对于历史栈的处理
      async function handleEffectTags(options, data) {
        const nowId = `${options.mainCid}-${options.refType}`;
        const oldList = historyData.list;
        let needGoing = false;
        // 这个地方要看看原来的里面有没有这个id
        if (!(historyData && getType(historyData) === 'object' && historyData?.actId)) {
          needGoing = true;
        } else {
          const booleanHas = oldList.some((item) => item.id === nowId);
          if (booleanHas) {
            // 切换下，已经存在的话，只切换就可以了
            preSetHistoryData({ list: oldList, actId: nowId });
            setActivityId(nowId);
          } else {
            needGoing = true;
          }
        }

        if (needGoing) {
          const nowCurrentItem = {
            id: nowId,
            mainCid: options.mainCid,
            mainCidName: data['list'][0].mainCidName,
            mainCidLevel: options.mainCidLevel,
            mainCidLevelName:
              options.mainCidLevel === 3
                ? '三级类目'
                : options.mainCidLevel === 2
                ? '二级类目'
                : options.mainCidLevel === 1
                ? '一级类目'
                : '',
            refType: options.refType,
            refTypeName: CATEREFTYPEENUM[options.refType].name,
          };

          let newList = [];
          if (oldList.length >= 20) {
            newList = [...oldList.slice(1), nowCurrentItem];
          } else {
            newList = [...oldList, nowCurrentItem];
          }
          preSetHistoryData({ list: newList, actId: nowId });
          setActivityId(nowId);
        }
      }
    },
    [historyData, preSetHistoryData, subForm]
  );

  // 二级筛选
  async function handleOnSubFormFish(changedValues, allValues) {
    const { deptId, cidScope } = allValues;
    const newDeptId = isEffectArray(deptId) ? deptId : '';
    setOptions((pre) => ({ ...pre, deptId: newDeptId, cidScope, isNew: false }));
  }

  // 驱动子组件调取api获取数据的途径【一级的话不用这个】
  const memoizedGetTableList = useCallback(async () => {
    const { isNew, ...trueOptions } = options;
    // 如果是新增驱动的设置，这个地方禁止继续操作，这样保证当前的驱动都是二级赛选条件变化引起的重新请求
    if (isNew) {
      return false;
    }

    if (!(trueOptions?.mainCid && trueOptions?.mainCidLevel && (trueOptions?.refType ?? '') !== '')) {
      setActivityId('');
      setCrossContentData(null);
      return false;
    }
    // 过滤掉非有效的字段【为空的或者空数组、空对象的字段】
    const utilOptions = cutNullOptions(trueOptions);
    try {
      logSearchActionParams(utilOptions);
      const { ret, data, msg } = await apis.technicaCateRecList({ ...utilOptions });
      if (ret && ret === '0') {
        setCrossContentData(data);
      } else {
        // 新增时，异常不要清空下方卡片
        if (!isNew) {
          setCrossContentData(null);
        }
        message.error(`${msg || '暂无匹配结果,请更换品类查询'}`);
      }
    } catch (error) {
      console.log(error);
    }
  }, [options]);
  useEffect(() => {
    memoizedGetTableList();
  }, [memoizedGetTableList]);

  // 排序回调
  function operationCallback(type, data) {
    switch (type) {
      case 'SORTERORDERCHANGE':
        setOptions((pre) => ({ ...pre, sortField: data.sortField, sortOrder: data.sortOrder, isNew: false }));
        break;
      default:
        break;
    }
  }

  /***********************************************************
   ************************ 交互事件响应 ***********************
   ***********************************************************/
  // 切换面板
  function onChange(targetKey) {
    setActivityId(targetKey);
    preSetHistoryData({ list: historyData.list, actId: targetKey });
    const currentItem = historyData.list.find((item) => item.id === targetKey);
    subForm.resetFields();
    setOptions((pre) => ({
      ...pre,
      ...optionsCommon,
      sortField: pre.sortField,
      sortOrder: pre.sortOrder,
      mainCid: currentItem?.mainCid,
      mainCidLevel: currentItem?.mainCidLevel,
      refType: currentItem?.refType,
      isNew: false,
    }));
  }
  // 删除
  function onEdit(targetKey, action) {
    if (action === 'remove') {
      const newTabsList = historyData.list.filter((item) => item.id !== targetKey);
      if (targetKey === activityId) {
        // 注意这里面有个特殊情况，就是当前就一个元素，删除以后，数据空了，要特殊处理
        if (isEffectArray(newTabsList)) {
          const currentItem = newTabsList[0];
          const newActId = currentItem.id;
          setActivityId(newActId);
          preSetHistoryData({ list: newTabsList, actId: newActId });
          subForm.resetFields();
          setOptions((pre) => ({
            ...pre,
            ...optionsCommon,
            sortField: pre.sortField,
            sortOrder: pre.sortOrder,
            mainCid: currentItem?.mainCid,
            mainCidLevel: currentItem?.mainCidLevel,
            refType: currentItem?.refType,
            isNew: false,
          }));
        } else {
          setActivityId('');
          preSetHistoryData({ list: [], actId: '' });
          subForm.resetFields();
          setOptions((pre) => ({
            ...optionsCommon,
            sortField: pre.sortField,
            sortOrder: pre.sortOrder,
            isNew: false,
          }));
        }
      } else {
        preSetHistoryData({ list: newTabsList, actId: historyData?.actId });
      }
    }
  }

  // 导出
  async function handleExportExcel() {
    if (!crossContentData) {
      message.error(`${'暂无匹配结果，请您稍后重试！'}`);
      return;
    }
    const { isNew, ...trueOptions } = options;
    // 过滤掉非有效的字段【为空的或者空数组、空对象的字段】
    const utilOptions = cutNullOptions(trueOptions);
    const { ret, data, msg } = await apis.technicaCateRecExport({ ...utilOptions });
    if (ret && ret === '0') {
      data?.url && window.open(data?.url);
    } else {
      message.error(`${msg || '导出失败，请您稍后重试!'}`);
    }
  }

  // 页面关闭上报历史记录card的个数
  const refHistoryData = useRef(null);
  refHistoryData.current = historyData?.list;
  useEffect(() => {
    return () => {
      logRemainCardNum(refHistoryData.current?.length);
    };
  }, []);

  return (
    <div className="cross-category-marketing-fa">
      {/* 页面查询器 */}
      <div className="cross-category-filter-fa">
        <ParentFilter parentForm={parentForm} categoryList={categoryList} handleOnFinish={handleOnParentFormFinish} />
      </div>

      {/* 页面内容 */}
      <div className="cross-content-fa">
        <div className="cross-content-title">
          <span className="title-h1">已推荐（{`${historyData?.list?.length || 0}`}）</span>
          <span className="title-dis">最多保留20个查询记录（超出将自动替换）</span>
        </div>
        <div className="cross-content-container">
          {hasLoad &&
            (isEffectArray(historyData?.list) ? (
              <>
                <div className="cross-content-history-list">
                  <div className="cross-group-tab">
                    <Tabs
                      hideAdd
                      type="editable-card"
                      activeKey={activityId}
                      onChange={onChange}
                      onEdit={onEdit}
                      tabBarStyle={{ marginBottom: 0 }}
                    >
                      {historyData?.list?.map((item) => {
                        return (
                          <Tabs.TabPane
                            key={item?.id}
                            tab={
                              <div
                                className={classnames(
                                  'cross-tab-head-fa',
                                  item.id === activityId ? 'cross-tab-head-choose-fa' : ''
                                )}
                              >
                                <div className="cross-tab-title">{item?.mainCidName} </div>
                                <div className="cross-tab-dis">{`${item?.mainCidLevelName}&${item?.refTypeName}`} </div>
                              </div>
                            }
                          />
                        );
                      })}
                    </Tabs>
                  </div>
                </div>
                <div className="cross-content-main-fa">
                  <div className="cross-sub-filter-fa">
                    <div className="cross-sub-filter-wrapper">
                      <Form
                        form={subForm}
                        initialValues={{ deptId: '', cidScope: CIDSCOPEENUM['0'].key }}
                        onValuesChange={handleOnSubFormFish}
                      >
                        <Form.Item label="推荐范围" name="deptId" style={{ marginBottom: 8 }}>
                          <Checkbox.Group className="group-checkbox-item-fa">
                            {isEffectArray(DEPTIDENUM) &&
                              DEPTIDENUM?.map((item) => {
                                return (
                                  <Checkbox key={item.key} value={item.key} className="group-checkbox-item">
                                    {item.name}
                                  </Checkbox>
                                );
                              })}
                          </Checkbox.Group>
                        </Form.Item>

                        <Form.Item label="品类类型" name="cidScope">
                          <Radio.Group placeholder="请选择商品商品类型">
                            {Object.values(CIDSCOPEENUM) &&
                              Object.values(CIDSCOPEENUM)?.map((item) => {
                                return (
                                  <Radio value={item.key} key={item.key}>
                                    {item.name}
                                  </Radio>
                                );
                              })}
                          </Radio.Group>
                        </Form.Item>
                      </Form>
                    </div>
                    <div className="cross-export-fa">
                      <ComponentLog>
                        <Button clstag="yx_center_kplyx_1668603027997|3" onClick={handleExportExcel}>
                          导出匹配结果
                        </Button>
                      </ComponentLog>
                    </div>
                  </div>

                  {/* table展示 */}
                  <div className="cross-content-show">
                    {<CrossContentTable tableData={crossContentData} operationCallback={operationCallback} />}
                  </div>
                </div>
              </>
            ) : (
              <CrossEmptyList />
            ))}
        </div>
      </div>

      {/* 用户引导 */}
      <CrossUserGuide ref={userGuideRef} />
    </div>
  );
};

export default CrossCategoryMarketing;
